import styled from "@emotion/styled"
import { spacingStaticMd, spacingStaticXl } from "@porsche-design-system/components-react/emotion"
import type { DatePrice, FormattedMsrp } from "@slfinrtl/ui-bff-routes"
import { Flex } from "src/components/Flex"
import { CheckboxField, FormLayout, InputField } from "src/components/form"
import { GridItem } from "src/components/grid/GridItem"
import { withErrorHandler } from "src/context/errorHandler/withErrorHandler"
import { gridSize } from "../../../common/gridSize.const"
import { Msrp } from "../../components"
import { PricingFormGrid } from "../../components/PricingFormGrid"
import { PriceHistory } from "../../components/priceHistory/PriceHistory"
import type { CalculatedPriceBreakdownView } from "../CalculatedPriceBreakdownView"
import type { ExtraCostsPriceBreakdownPricingFields } from "../extraCostsPriceBreakdownPricing.fields"
import { ExtraCostsPriceBreakdownPriceSelectorField } from "./ExtraCostsPriceBreakdownPriceSelectorField"

interface ExtraCostsPriceBreakdownPricingFormProps {
	calculatedPriceBreakdownView: CalculatedPriceBreakdownView
	fields: ExtraCostsPriceBreakdownPricingFields
	formattedMsrp?: FormattedMsrp
	onExtraCostToggle: (isChecked: boolean) => void
	priceHistory: DatePrice[] | undefined
}

export const ExtraCostsPriceBreakdownPricingForm = withErrorHandler(
	({
		calculatedPriceBreakdownView,
		fields,
		formattedMsrp,
		onExtraCostToggle,
		priceHistory,
	}: ExtraCostsPriceBreakdownPricingFormProps) => {
		return (
			<PricingFormGrid>
				<GridItemFormLayout size={gridSize}>
					<InputField {...fields.vehiclePrice} />
					{fields.vatDeductible && (
						<VatDeductibleCheckboxField
							{...fields.vatDeductible}
							disabledHint={fields.vatDeductible.disabledHint}
						/>
					)}
					<InputField {...fields.minimumPrice} />
					<InputField {...fields.purchasePrice} />

					<StyledExtraCostsPriceBreakdownPriceSelectorField
						calculatedPriceBreakdownView={calculatedPriceBreakdownView}
						onExtraCostToggle={onExtraCostToggle}
						{...fields.extraCosts}
					/>
				</GridItemFormLayout>

				<PriceHistoryContainer size={gridSize} $direction="column">
					{priceHistory && priceHistory.length > 0 && <PriceHistory view={priceHistory} />}
					{formattedMsrp && <Msrp view={formattedMsrp} />}
				</PriceHistoryContainer>
			</PricingFormGrid>
		)
	},
)

const StyledFlex = styled(Flex)`
	gap: ${spacingStaticXl};
`

const StyledFormLayout = styled(FormLayout)`
	align-self: start; /* needed when price history is larger than form, otherwise gaps between inputs increase */
`

const GridItemFormLayout = StyledFormLayout.withComponent(GridItem)
const PriceHistoryContainer = StyledFlex.withComponent(GridItem)

const VatDeductibleCheckboxField = styled(CheckboxField)`
	margin-bottom: ${spacingStaticMd};
`

const StyledExtraCostsPriceBreakdownPriceSelectorField = styled(ExtraCostsPriceBreakdownPriceSelectorField)`
	margin-top: ${spacingStaticMd};
`
